import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Wallet as WalletIcon, Loader2, ArrowDownLeft, ArrowUpRight, Plus } from "lucide-react";
import { useLocation } from "wouter";
import { useWalletBalance, useExpiringSummary } from "@/hooks/use-wallet";
import { apiClient } from "@/lib/api-client";
import type { WalletTransaction } from "@/lib/types";
import { Skeleton } from "@/components/ui/skeleton";

export default function Wallet() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [, navigate] = useLocation();

  const { data: walletData, isLoading: balanceLoading } = useWalletBalance();
  const balance = walletData?.balance ?? 0;
  const currency = walletData?.currency ?? t("services.currency");

  const { data: expiringSummary } = useExpiringSummary();

  const { data: transactionsRaw, isLoading: txLoading } = useQuery({
    queryKey: ["/wallets/transactions", 1, 20],
    queryFn: async (): Promise<WalletTransaction[]> => {
      const response = await apiClient.get("/wallets/transactions?page=1&limit=20");
      if (!response.ok) {
        throw new Error("Failed to fetch transactions");
      }
      const json = await response.json();
      // Handle both { data: [...] } and raw array shapes.
      if (Array.isArray(json)) return json;
      if (json && Array.isArray(json.data)) return json.data;
      return [];
    },
    enabled: !!apiClient.getToken(),
  });

  const transactions = transactionsRaw ?? [];

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="text-2xl font-bold flex items-center gap-2 mb-6">
          <WalletIcon className="w-6 h-6 text-primary" />
          {t("walletPage.title")}
        </h1>

        {/* Balance display */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-6 mb-6">
          <p className="text-sm opacity-90">{t("walletPage.balance")}</p>
          {balanceLoading ? (
            <Skeleton className="h-10 w-40 mt-2 bg-primary-foreground/20" />
          ) : (
            <p className="text-4xl font-bold mt-1">
              {balance.toFixed(3)} <span className="text-2xl font-medium opacity-90">{currency}</span>
            </p>
          )}
          {!balanceLoading && expiringSummary && expiringSummary.totalExpiring > 0 && expiringSummary.nearestExpiry && (
            <p className="text-xs mt-2 opacity-90">
              {expiringSummary.totalExpiring.toFixed(3)} {currency} —{" "}
              {t("walletPage.expiresOn", {
                date: format(
                  new Date(expiringSummary.nearestExpiry),
                  isRtl ? "d MMM yyyy" : "MMM d, yyyy",
                  { locale: isRtl ? ar : enUS },
                ),
              })}
            </p>
          )}
        </div>

        {/* Top Up button */}
        <button
          onClick={() => navigate("/topup")}
          className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 mb-6"
        >
          <Plus className="h-5 w-5" />
          {t("walletPage.topUp")}
        </button>

        {/* Transaction history */}
        <h2 className="font-semibold text-lg mb-3">{t("walletPage.transactions")}</h2>

        {txLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
            <WalletIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t("walletPage.noTransactions")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((tx) => {
              const isCredit = tx.type === "CREDIT";
              const TxIcon = isCredit ? ArrowDownLeft : ArrowUpRight;
              const iconColor = isCredit
                ? "text-green-600 dark:text-green-400 bg-green-500/10"
                : "text-red-600 dark:text-red-400 bg-red-500/10";
              return (
                <div
                  key={tx.id}
                  className="bg-card rounded-xl border border-border/50 p-4 flex items-center gap-3"
                  data-testid="wallet-transaction"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconColor}`}>
                    <TxIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">
                      {isCredit ? t("walletPage.credit") : t("walletPage.debit")}
                    </p>
                    {(tx.description || tx.reason) && (
                      <p className="text-xs text-muted-foreground truncate">
                        {tx.description || tx.reason}
                      </p>
                    )}
                    {tx.createdAt && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {format(new Date(tx.createdAt), "PPP", { locale: isRtl ? ar : enUS })}
                      </p>
                    )}
                    {isCredit && tx.expiresAt && (
                      <span
                        className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded ${
                          tx.lotStatus === "EXPIRED"
                            ? "bg-red-500/10 text-red-600 dark:text-red-400"
                            : "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                        }`}
                      >
                        {tx.lotStatus === "EXPIRED"
                          ? t("walletPage.expired")
                          : `${t("walletPage.validUntil")} ${format(new Date(tx.expiresAt), "d MMM yyyy", { locale: isRtl ? ar : enUS })}`}
                      </span>
                    )}
                  </div>
                  <div className="text-end flex-shrink-0">
                    <p
                      className={`font-semibold ${
                        isCredit
                          ? "text-green-600 dark:text-green-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {isCredit ? "+" : "-"}
                      {Number(tx.amount).toFixed(3)} {tx.currency || currency}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
